'use client';

/**
 * Lifecycle for an admin **support-scope landing** — the `?support=1&tenantId&
 * restaurantId[&handoff]` URL the admin app opens the management app with.
 *
 * Three components need to agree on "is a support session still being
 * established in this tab?", and they need to agree for the whole duration —
 * not just for as long as the query string happens to be present:
 *
 *   - `SupportScopeLanding` performs the landing.
 *   - `AuthGuard` must not bolt to the auth app mid-landing.
 *   - `AuthenticatedSelectionGuard` must not paint, auto-select, or (the bug
 *     this module exists for) DELETE the tenant/restaurant selection while the
 *     landing is still in flight.
 *
 * Sniffing `window.location.search` at each call site cannot express that: the
 * landing strips its own params on success, so the answer flips from "yes" to
 * "no" at a moment unrelated to whether the session is actually ready — and
 * three copies of the same sniff drifted apart. So the request is **latched on
 * first read** (before anything can strip it) and the phase is owned here:
 *
 *   none → the tab was not opened by a support landing. Everything behaves
 *          exactly as it did before this module existed.
 *   pending → a landing is in flight. Consumers hold.
 *   settled → the landing finished, succeeded or not. Consumers resume.
 *
 * A watchdog force-settles after {@link SUPPORT_LANDING_MAX_MS} so a landing
 * that dies without running its own `finally` (an unhandled module error, a
 * killed request) degrades to the app's normal behaviour instead of holding a
 * skeleton on screen forever.
 */

const SUPPORT_PARAM = 'support';
const TENANT_PARAM = 'tenantId';
const LOCATION_PARAM = 'restaurantId';
const HANDOFF_PARAM = 'handoff';

/** Every param the landing owns — stripped together on success. */
const LANDING_PARAMS = [
  SUPPORT_PARAM,
  TENANT_PARAM,
  LOCATION_PARAM,
  HANDOFF_PARAM,
] as const;

/**
 * Hard ceiling on how long consumers will hold for a landing. Generous — the
 * landing does a redeem round-trip, a custom-token sign-in, a forced token
 * refresh, a `/me/locations` refetch and two Firestore reads — but bounded,
 * because a hold that cannot end is an infinite skeleton.
 */
export const SUPPORT_LANDING_MAX_MS = 20_000;

/** The scope an admin asked to land in, parsed from the URL. */
export interface SupportLandingRequest {
  /**
   * Support-scope only, and therefore OPTIONAL.
   *
   * A classic user masquerade has no scope to seed: the target's own
   * memberships drive selection, exactly as they do when that user signs in
   * themselves. Requiring these would have made the landing refuse to run for
   * `mode: 'user'` — which is why they are nullable rather than absent.
   */
  readonly tenantId: number | null;
  readonly restaurantId: number | null;
  /**
   * One-time handoff code forwarded by the admin app. Absent when a stale admin
   * tab dropped it — the redeem then falls back to "this admin's most recent
   * handoff" server-side.
   */
  readonly handoffId?: string;
}

export type SupportLandingPhase = 'none' | 'pending' | 'settled';
export type SupportLandingOutcome = 'succeeded' | 'failed';

let phase: SupportLandingPhase | null = null;
let request: SupportLandingRequest | null = null;
let outcome: SupportLandingOutcome | null = null;
let watchdog: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Parse the URL exactly once, the first time anything asks. Deliberately does
 * NOT latch during SSR: the server render has no query string of its own to
 * latch from, and latching `none` there would be inherited by nothing anyway
 * (module state is per-process on the server, per-load in the browser).
 */
function ensureLatched(): void {
  if (phase !== null) return;
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  if (params.get(SUPPORT_PARAM) !== '1') {
    phase = 'none';
    return;
  }

  // A scope is OPTIONAL: present for support-scope, absent for a classic user
  // masquerade. Present-but-unusable is still refused — a half-specified scope
  // means a caller built the URL wrong, and holding the app for a session
  // nobody can establish is worse than treating the tab as ordinary.
  const hasScopeParams =
    params.has(TENANT_PARAM) || params.has(LOCATION_PARAM);
  let tenantId: number | null = null;
  let locationNumericId: number | null = null;
  if (hasScopeParams) {
    const t = Number(params.get(TENANT_PARAM));
    const r = Number(params.get(LOCATION_PARAM));
    if (!Number.isFinite(t) || !Number.isFinite(r) || t <= 0 || r <= 0) {
      phase = 'none';
      return;
    }
    tenantId = t;
    locationNumericId = r;
  }

  request = {
    tenantId,
    restaurantId: locationNumericId,
    handoffId: params.get(HANDOFF_PARAM) ?? undefined,
  };
  phase = 'pending';
  watchdog = setTimeout(() => {
    settleSupportLanding('failed');
  }, SUPPORT_LANDING_MAX_MS);
}

/** The scope this tab was opened to land in, or `null` when it wasn't. */
export function getSupportLandingRequest(): SupportLandingRequest | null {
  ensureLatched();
  return request;
}

/** True while a support session is still being established in this tab. */
export function isSupportLandingPending(): boolean {
  ensureLatched();
  return phase === 'pending';
}

/**
 * True when this tab was opened by a support landing at all — regardless of
 * whether it has settled. Used by `AuthGuard`, which must know that its session
 * is being established out-of-band even after the landing finishes.
 */
export function isSupportLanding(): boolean {
  ensureLatched();
  return phase !== 'none' && phase !== null;
}

/** How the landing ended, or `null` while it is still in flight. */
export function getSupportLandingOutcome(): SupportLandingOutcome | null {
  ensureLatched();
  return outcome;
}

/**
 * Release every consumer holding for this landing. Idempotent — the watchdog
 * and the landing's own `finally` both call it, and whichever runs first wins.
 */
export function settleSupportLanding(result: SupportLandingOutcome): void {
  ensureLatched();
  if (phase !== 'pending') return;
  phase = 'settled';
  outcome = result;
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
  notify();
}

/**
 * Strip the landing params from the address bar.
 *
 * Called ONLY on success. On failure the params stay, so a plain browser reload
 * is a genuine retry of the landing — the previous behaviour stripped them in a
 * `finally` regardless of outcome, which made every failure terminal and left
 * "start a whole new session from the admin app" as the only recovery.
 */
export function clearSupportLandingParams(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const key of LANDING_PARAMS) {
      if (params.has(key)) {
        params.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const qs = params.toString();
    window.history.replaceState(
      null,
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
    );
  } catch {
    // Non-fatal: the phase latch (not the URL) is what stops a re-run.
  }
}

/** Subscribe to phase changes. Returns an unsubscribe function. */
export function subscribeToSupportLanding(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * TEST ONLY — reset the module latch between cases. Never called by app code;
 * the latch is deliberately one-shot per page load.
 */
export function __resetSupportLandingForTests(): void {
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
  phase = null;
  request = null;
  outcome = null;
  listeners.clear();
}
